// QbankTemplateForm
// Reusable form for editing a single Question-bank template (mcq /
// true-false / multi-mcq / cloze / ordering / bucket-sort / matching /
// fixed). Stateless — parent owns the template object and applies
// patches from onChange.
//
// Used by FacilitatorMcqEditor (Question-bank sets),
// FacilitatorRevisionEditor (curated revision exercises) and
// FacilitatorQuestionBankItemEditor (per-type item library) so the
// authoring UX stays consistent.

import React from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import { FRAMINGS, FRAMING_LABELS, countClozeMarkers } from '../../lib/mcq';

// Concrete worked example for the auto-generated calculation editor.
// Used by both the inline walkthrough panel ("here's what one looks
// like") and the Load example button ("prefill the form with this so
// the lecturer can edit it instead of building from scratch").
const CALC_EXAMPLE = {
  prompt: 'A patient weighs {weight} kg. They need paracetamol at {dose} mg/kg. What total dose in mg?',
  formula: 'weight * dose',
  unit: 'mg',
  tolerance: { type: 'percent', value: 2 },
  variables: [
    { name: 'weight', label: 'Patient weight', min: 40, max: 100, step: 5, unit: 'kg' },
    { name: 'dose', label: 'Dose per kg', min: 10, max: 20, step: 5, unit: 'mg/kg' },
  ],
};

const QbankTemplateForm = ({ template, onChange, idPrefix = 'tpl' }) => {
  const templateType = template.templateType || 'mcq';
  const isMulti = templateType === 'multi-mcq';
  const isTrueFalse = templateType === 'true-false';
  const isCloze = templateType === 'cloze';
  const isOrdering = templateType === 'ordering';
  const isBucketSort = templateType === 'bucket-sort';
  const isMatching = templateType === 'matching';
  const isFixed = templateType === 'fixed';
  const isCalc = templateType === 'calculation';
  const supportsFraming = templateType === 'mcq' || templateType === 'multi-mcq';
  const clozeMarkerCount = isCloze ? countClozeMarkers(template.prompt) : 0;
  const clozeMarkerMismatch = isCloze
    && clozeMarkerCount !== (Array.isArray(template.blanks) ? template.blanks.length : 0);

  const patch = (delta) => onChange({ ...template, ...delta });
  // Ordering helpers
  const newOrderingId = () => `o-${Math.random().toString(36).slice(2, 8)}`;
  const updateOrderingItem = (i, text) => patch({
    items: (template.items || []).map((it, idx) => (idx === i ? { ...it, text } : it)),
  });
  const addOrderingItem = () => patch({
    items: [...(template.items || []), { id: newOrderingId(), text: '' }],
  });
  const removeOrderingItem = (i) => patch({
    items: (template.items || []).filter((_, idx) => idx !== i),
  });
  const moveOrderingItem = (i, dir) => {
    const list = (template.items || []).slice();
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    [list[i], list[j]] = [list[j], list[i]];
    patch({ items: list });
  };
  // Bucket-sort helpers
  const newBucketId = () => `b-${Math.random().toString(36).slice(2, 8)}`;
  const newBucketItemId = () => `s-${Math.random().toString(36).slice(2, 8)}`;
  const updateBucketLabel = (i, label) => patch({
    buckets: (template.buckets || []).map((b, idx) => (idx === i ? { ...b, label } : b)),
  });
  const addBucket = () => patch({
    buckets: [...(template.buckets || []), { id: newBucketId(), label: '' }],
  });
  const removeBucket = (i) => {
    const list = (template.buckets || []).filter((_, idx) => idx !== i);
    const removedId = (template.buckets || [])[i]?.id;
    // Reassign orphaned items to the first remaining bucket so the
    // exercise stays valid; if there are no buckets left, drop bucketId.
    const fallback = list[0]?.id || '';
    const items = (template.items || []).map((it) =>
      it.bucketId === removedId ? { ...it, bucketId: fallback } : it);
    patch({ buckets: list, items });
  };
  const updateBucketItem = (i, delta) => patch({
    items: (template.items || []).map((it, idx) => (idx === i ? { ...it, ...delta } : it)),
  });
  const addBucketItem = () => {
    const firstBucket = (template.buckets || [])[0]?.id || '';
    patch({ items: [...(template.items || []), { id: newBucketItemId(), text: '', bucketId: firstBucket }] });
  };
  const removeBucketItem = (i) => patch({
    items: (template.items || []).filter((_, idx) => idx !== i),
  });
  // Matching helpers
  const newPairId = () => `m-${Math.random().toString(36).slice(2, 8)}`;
  const updatePair = (i, delta) => patch({
    pairs: (template.pairs || []).map((p, idx) => (idx === i ? { ...p, ...delta } : p)),
  });
  const addPair = () => patch({
    pairs: [...(template.pairs || []), { id: newPairId(), left: '', right: '' }],
  });
  const removePair = (i) => patch({
    pairs: (template.pairs || []).filter((_, idx) => idx !== i),
  });
  const setOption = (optionIndex, value) => patch({
    options: (template.options || []).map((o, i) => (i === optionIndex ? value : o)),
  });
  const addOption = () => patch({ options: [...(template.options || []), ''] });
  const removeOption = (optionIndex) => {
    const nextOptions = (template.options || []).filter((_, i) => i !== optionIndex);
    if (isMulti) {
      const next = (template.correctIndices || [])
        .filter((idx) => idx !== optionIndex)
        .map((idx) => (idx > optionIndex ? idx - 1 : idx));
      patch({ options: nextOptions, correctIndices: next.length ? next : [0] });
      return;
    }
    const wasCorrect = optionIndex === Number(template.correctIndex);
    const nextCorrect = wasCorrect ? 0
      : (Number(template.correctIndex) > optionIndex ? Number(template.correctIndex) - 1 : Number(template.correctIndex));
    patch({ options: nextOptions, correctIndex: nextCorrect });
  };
  const toggleMultiCorrect = (optionIndex) => {
    const list = Array.isArray(template.correctIndices) ? template.correctIndices : [];
    const next = list.includes(optionIndex)
      ? list.filter((idx) => idx !== optionIndex)
      : [...list, optionIndex].sort((a, b) => a - b);
    patch({ correctIndices: next });
  };
  const updateBlank = (blankIndex, blankPatch) => {
    const blanks = Array.isArray(template.blanks) ? template.blanks : [];
    patch({ blanks: blanks.map((b, bi) => (bi === blankIndex ? { ...b, ...blankPatch } : b)) });
  };
  const addBlank = () => {
    const blanks = Array.isArray(template.blanks) ? template.blanks : [];
    patch({ blanks: [...blanks, { answers: [''], caseSensitive: false }] });
  };
  const removeBlank = (blankIndex) => {
    const blanks = (Array.isArray(template.blanks) ? template.blanks : []).filter((_, bi) => bi !== blankIndex);
    patch({ blanks: blanks.length ? blanks : [{ answers: [''], caseSensitive: false }] });
  };

  // Calculation (formula) helpers — variables array of
  // { name, label, min, max, step, unit }.
  const emptyVariable = () => ({ name: '', label: '', min: 0, max: 10, step: 1, unit: '' });
  const updateVariable = (i, vPatch) => patch({
    variables: (template.variables || []).map((v, idx) => (idx === i ? { ...v, ...vPatch } : v)),
  });
  const addVariable = () => patch({
    variables: [...(template.variables || []), emptyVariable()],
  });
  const removeVariable = (i) => patch({
    variables: (template.variables || []).filter((_, idx) => idx !== i),
  });
  const loadCalcExample = () => patch({ ...CALC_EXAMPLE, notes: template.notes || '' });
  // Walkthrough panel auto-opens when the form is empty so the lecturer
  // sees the worked example before they start building.
  const calcFormEmpty = isCalc
    && !String(template.formula || '').trim()
    && (Array.isArray(template.variables) ? template.variables.length === 0 : true);

  return (
    <>
      <Form.Group className="mb-3">
        <Form.Label>Prompt</Form.Label>
        <Form.Control as="textarea" rows={isCloze ? 3 : 2} value={template.prompt || ''}
          onChange={(e) => patch({ prompt: e.target.value })}
          placeholder={isTrueFalse
            ? 'Naproxen is the safest NSAID in patients with significant cardiovascular risk.'
            : isCloze
              ? 'Paracetamol 1 g is given every {{1}} hours, up to a daily max of {{2}} g.'
              : isFixed
                ? 'A patient weighing 70 kg needs paracetamol 15 mg/kg. What dose in mg?'
                : isCalc
                  ? 'A patient weighing {weight} kg is prescribed paracetamol at {dose} mg/kg. What dose in mg?'
                  : 'A 65-year-old man is admitted with community-acquired pneumonia and documented penicillin anaphylaxis. Which empirical antibiotic is most appropriate?'}
        />
        {isCalc ? (
          <Form.Text className="text-muted">
            Use <code>{'{name}'}</code> placeholders for variables — the engine substitutes a fresh sampled value each play.
          </Form.Text>
        ) : null}
        {isCloze ? (
          <Form.Text className="text-muted">
            Use <code>{'{{1}}'}</code>, <code>{'{{2}}'}</code>, … to mark blanks. The numbers correspond to the blanks listed below. {clozeMarkerCount} marker{clozeMarkerCount === 1 ? '' : 's'} detected.
          </Form.Text>
        ) : null}
      </Form.Group>

      {isFixed ? (
        <Row className="g-2 mb-2">
          <Form.Group as={Col} md={4}>
            <Form.Label className="small text-muted">Expected answer (number)</Form.Label>
            <Form.Control type="number" step="any" placeholder="1050"
              value={template.expectedAnswer ?? ''}
              onChange={(e) => patch({ expectedAnswer: e.target.value })} />
          </Form.Group>
          <Form.Group as={Col} md={4}>
            <Form.Label className="small text-muted">Unit (optional)</Form.Label>
            <Form.Control placeholder="mg"
              value={template.unit || ''}
              onChange={(e) => patch({ unit: e.target.value })} />
          </Form.Group>
          <Form.Group as={Col} md={4}>
            <Form.Label className="small text-muted">Tolerance</Form.Label>
            <div className="d-flex gap-2">
              <Form.Select value={template.tolerance?.type || 'percent'}
                onChange={(e) => patch({ tolerance: { ...(template.tolerance || {}), type: e.target.value } })}>
                <option value="percent">±%</option>
                <option value="absolute">± absolute</option>
                <option value="exact">Exact</option>
              </Form.Select>
              <Form.Control type="number" step="any" min={0}
                disabled={template.tolerance?.type === 'exact'}
                value={template.tolerance?.value ?? 1}
                onChange={(e) => patch({ tolerance: { ...(template.tolerance || {}), value: Number(e.target.value) } })} />
            </div>
          </Form.Group>
        </Row>
      ) : isCalc ? (
        <div className="mb-2">
          <details className="card mb-3 editor-help-panel" open={calcFormEmpty}>
            <summary className="card-body py-2 d-flex align-items-center gap-2 editor-help-panel__summary">
              <i className="bi bi-lightbulb text-warning" aria-hidden="true" />
              <strong>What is an auto-generated calculation?</strong>
              <span className="ms-auto small text-muted">click to {calcFormEmpty ? 'collapse' : 'expand'}</span>
            </summary>
            <div className="card-body pt-0 editor-help-panel__body">
              <p className="mb-2">
                You write <strong>one template</strong> with placeholders for the numbers that change. Each time a student plays, the engine picks new random values, slots them into the prompt, and computes the expected answer using your formula. Two students see different numbers but the same skill is tested.
              </p>
              <p className="mb-1 fw-semibold">Worked example — weight-based paracetamol dose:</p>
              <ol className="mb-3 small">
                <li>
                  <strong>Prompt</strong> (with <code>{'{name}'}</code> placeholders for the numbers that change):<br />
                  <code>{CALC_EXAMPLE.prompt}</code>
                </li>
                <li>
                  <strong>Variables</strong> — one row per placeholder. Min / Max / Step define the range of values the engine samples.<br />
                  <code>weight</code>: 40–100 kg, step 5{' · '}
                  <code>dose</code>: 10–20 mg/kg, step 5
                </li>
                <li>
                  <strong>Formula</strong> — how to compute the expected answer from the variables:<br />
                  <code>weight * dose</code>
                </li>
                <li>
                  <strong>Tolerance</strong> — how close the student's answer needs to be: ±2% lets them round normally and still mark correct.
                </li>
              </ol>
              <div className="bg-light border rounded p-2 small mb-3">
                <i className="bi bi-arrow-right text-success me-1" />
                A student might play this and see: <em>"A patient weighs 70 kg. They need paracetamol at 15 mg/kg. What total dose in mg?"</em>{' '}
                The engine computes <code>70 × 15 = 1050 mg</code> and accepts any answer between 1029 and 1071 mg.
              </div>
              <Button type="button" size="sm" variant="primary" onClick={loadCalcExample}>
                <i className="bi bi-magic me-1" />Load this example into the form
              </Button>
              <span className="small text-muted ms-2">You can then edit it to suit your own scenario.</span>

              <details className="mt-3">
                <summary className="small fw-semibold">Optional: rounding and other tweaks for trickier sums</summary>
                <p className="small text-muted mt-2 mb-2">
                  For most calculations you only need <code>+ − × ÷</code>. The functions below cover trickier cases — wrap them around your formula like <code>round(weight × 0.1)</code>.
                </p>
                <ul className="small mb-0">
                  <li><code>round(x)</code> — round to the nearest whole number. <em>e.g. round a 70 kg patient's 7.5 mg/kg dose from 525 mg to 525 mg.</em></li>
                  <li><code>floor(x)</code> — always round <strong>down</strong>. <em>e.g. number of whole vials needed for a dose.</em></li>
                  <li><code>ceil(x)</code> — always round <strong>up</strong>. <em>e.g. infusion bags required for a total volume.</em></li>
                  <li><code>min(a, b)</code> — the smaller of two values. <em>e.g. cap a dose at the adult maximum.</em></li>
                  <li><code>max(a, b)</code> — the larger of two values. <em>e.g. a minimum starting dose floor.</em></li>
                  <li><code>abs(x)</code> — strip the negative sign (always positive). Rarely needed in clinical calcs.</li>
                </ul>
              </details>
            </div>
          </details>
          <Row className="g-2 mb-2">
            <Form.Group as={Col} md={8}>
              <Form.Label className="small text-muted">Formula</Form.Label>
              <Form.Control placeholder="weight * dose"
                value={template.formula || ''}
                onChange={(e) => patch({ formula: e.target.value })} />
              <Form.Text className="text-muted">
                Use your variable names from below with <code>+</code> (add), <code>-</code> (subtract), <code>*</code> (multiply), <code>/</code> (divide), and <code>( )</code> for grouping. See the help panel above for rounding and other optional functions.
              </Form.Text>
            </Form.Group>
            <Form.Group as={Col} md={4}>
              <Form.Label className="small text-muted">Unit (optional)</Form.Label>
              <Form.Control placeholder="mg"
                value={template.unit || ''}
                onChange={(e) => patch({ unit: e.target.value })} />
            </Form.Group>
          </Row>
          <Row className="g-2 mb-2">
            <Form.Group as={Col} md={6}>
              <Form.Label className="small text-muted">Tolerance</Form.Label>
              <div className="d-flex gap-2">
                <Form.Select value={template.tolerance?.type || 'percent'}
                  onChange={(e) => patch({ tolerance: { ...(template.tolerance || {}), type: e.target.value } })}>
                  <option value="percent">±%</option>
                  <option value="absolute">± absolute</option>
                </Form.Select>
                <Form.Control type="number" step="any" min={0}
                  value={template.tolerance?.value ?? 1}
                  onChange={(e) => patch({ tolerance: { ...(template.tolerance || {}), value: Number(e.target.value) } })} />
              </div>
            </Form.Group>
          </Row>
          <h6 className="mt-3">Variables</h6>
          {(template.variables || []).length === 0 ? (
            <div className="text-muted small mb-2">No variables yet. Add one for each <code>{'{placeholder}'}</code> in the prompt and formula.</div>
          ) : (
            (template.variables || []).map((v, i) => (
              <Row key={i} className="g-2 align-items-end mb-2">
                <Form.Group as={Col} md={2}>
                  <Form.Label className="small text-muted">Name</Form.Label>
                  <Form.Control value={v.name || ''} placeholder="weight"
                    onChange={(e) => updateVariable(i, { name: e.target.value.replace(/[^a-zA-Z0-9_]/g, '') })} />
                </Form.Group>
                <Form.Group as={Col} md={3}>
                  <Form.Label className="small text-muted">Label (optional)</Form.Label>
                  <Form.Control value={v.label || ''} placeholder="Patient weight"
                    onChange={(e) => updateVariable(i, { label: e.target.value })} />
                </Form.Group>
                <Form.Group as={Col} md={2}>
                  <Form.Label className="small text-muted">Min</Form.Label>
                  <Form.Control type="number" step="any" value={v.min ?? 0}
                    onChange={(e) => updateVariable(i, { min: Number(e.target.value) })} />
                </Form.Group>
                <Form.Group as={Col} md={2}>
                  <Form.Label className="small text-muted">Max</Form.Label>
                  <Form.Control type="number" step="any" value={v.max ?? 0}
                    onChange={(e) => updateVariable(i, { max: Number(e.target.value) })} />
                </Form.Group>
                <Form.Group as={Col} md={1}>
                  <Form.Label className="small text-muted">Step</Form.Label>
                  <Form.Control type="number" step="any" value={v.step ?? 0}
                    onChange={(e) => updateVariable(i, { step: Number(e.target.value) })} />
                </Form.Group>
                <Form.Group as={Col} md={1}>
                  <Form.Label className="small text-muted">Unit</Form.Label>
                  <Form.Control value={v.unit || ''} placeholder="kg"
                    onChange={(e) => updateVariable(i, { unit: e.target.value })} />
                </Form.Group>
                <Col xs="auto">
                  <Button size="sm" variant="outline-danger" onClick={() => removeVariable(i)}>Del</Button>
                </Col>
              </Row>
            ))
          )}
          <Button type="button" size="sm" variant="outline-secondary" onClick={addVariable}>
            <i className="bi bi-plus-lg me-1" />Add variable
          </Button>
        </div>
      ) : isMatching ? (
        <div>
          <h6 className="mt-2">Pairs <span className="small text-muted">— the right column gets shuffled for the student</span></h6>
          {(template.pairs || []).map((p, i) => (
            <Row key={p.id || i} className="g-2 align-items-center mb-2">
              <Col>
                <Form.Control value={p.left || ''} placeholder={`Left ${i + 1}`}
                  onChange={(e) => updatePair(i, { left: e.target.value })} />
              </Col>
              <Col xs="auto" className="text-muted small fw-bold">↔</Col>
              <Col>
                <Form.Control value={p.right || ''} placeholder={`Right ${i + 1}`}
                  onChange={(e) => updatePair(i, { right: e.target.value })} />
              </Col>
              <Col xs="auto">
                <Button type="button" size="sm" variant="outline-danger" onClick={() => removePair(i)} disabled={(template.pairs || []).length <= 2}>Del</Button>
              </Col>
            </Row>
          ))}
          <Button type="button" size="sm" variant="outline-secondary" onClick={addPair}>
            <i className="bi bi-plus-lg me-1" />Add pair
          </Button>
          <Form.Check type="switch" id={`${idPrefix}-match-shuffle`} className="mt-3"
            label="Shuffle the right column for the student"
            checked={template.shuffleRight !== false}
            onChange={(e) => patch({ shuffleRight: e.target.checked })} />
          <Form.Text className="text-muted d-block mt-2">
            Partial credit: students score 1 per row whose right-side item lines up with its left pair, divided by total pairs.
          </Form.Text>
        </div>
      ) : isOrdering ? (
        <div>
          <h6 className="mt-2">Items <span className="small text-muted">— shown in the order below; shuffled for the student</span></h6>
          {(template.items || []).map((it, i) => (
            <Row key={it.id || i} className="g-2 align-items-center mb-2">
              <Col xs="auto" className="text-muted small fw-bold" style={{ width: '2rem' }}>{i + 1}.</Col>
              <Col>
                <Form.Control value={it.text || ''} placeholder={`Step ${i + 1}`}
                  onChange={(e) => updateOrderingItem(i, e.target.value)} />
              </Col>
              <Col xs="auto" className="d-flex gap-1">
                <Button type="button" size="sm" variant="outline-secondary" onClick={() => moveOrderingItem(i, -1)} disabled={i === 0} title="Move up">↑</Button>
                <Button type="button" size="sm" variant="outline-secondary" onClick={() => moveOrderingItem(i, 1)} disabled={i === (template.items || []).length - 1} title="Move down">↓</Button>
                <Button type="button" size="sm" variant="outline-danger" onClick={() => removeOrderingItem(i)} disabled={(template.items || []).length <= 2}>Del</Button>
              </Col>
            </Row>
          ))}
          <Button type="button" size="sm" variant="outline-secondary" onClick={addOrderingItem}>
            <i className="bi bi-plus-lg me-1" />Add item
          </Button>
          <Form.Check type="switch" id={`${idPrefix}-ord-shuffle`} className="mt-3"
            label="Shuffle items when the student sees them"
            checked={template.shuffleItems !== false}
            onChange={(e) => patch({ shuffleItems: e.target.checked })} />
          <Form.Text className="text-muted d-block mt-2">
            Partial credit: students score 1 per item in its correct position, divided by total items.
          </Form.Text>
        </div>
      ) : isBucketSort ? (
        <div>
          <h6 className="mt-2">Buckets</h6>
          {(template.buckets || []).map((b, i) => (
            <Row key={b.id || i} className="g-2 align-items-center mb-2">
              <Col xs="auto" className="text-muted small fw-bold" style={{ width: '2rem' }}>{String.fromCharCode(65 + i)}.</Col>
              <Col>
                <Form.Control value={b.label || ''} placeholder={`Bucket ${i + 1} label`}
                  onChange={(e) => updateBucketLabel(i, e.target.value)} />
              </Col>
              <Col xs="auto">
                <Button type="button" size="sm" variant="outline-danger" onClick={() => removeBucket(i)} disabled={(template.buckets || []).length <= 2}>Del</Button>
              </Col>
            </Row>
          ))}
          <Button type="button" size="sm" variant="outline-secondary" onClick={addBucket}>
            <i className="bi bi-plus-lg me-1" />Add bucket
          </Button>
          <h6 className="mt-3">Items</h6>
          {(template.items || []).map((it, i) => (
            <Row key={it.id || i} className="g-2 align-items-center mb-2">
              <Col>
                <Form.Control value={it.text || ''} placeholder={`Item ${i + 1}`}
                  onChange={(e) => updateBucketItem(i, { text: e.target.value })} />
              </Col>
              <Col xs="auto" style={{ minWidth: '12rem' }}>
                <Form.Select value={it.bucketId || ''}
                  onChange={(e) => updateBucketItem(i, { bucketId: e.target.value })}>
                  {(template.buckets || []).map((b) => (
                    <option key={b.id} value={b.id}>{b.label || '(unlabelled)'}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs="auto">
                <Button type="button" size="sm" variant="outline-danger" onClick={() => removeBucketItem(i)} disabled={(template.items || []).length <= 2}>Del</Button>
              </Col>
            </Row>
          ))}
          <Button type="button" size="sm" variant="outline-secondary" onClick={addBucketItem}>
            <i className="bi bi-plus-lg me-1" />Add item
          </Button>
          <Form.Check type="switch" id={`${idPrefix}-bs-shuffle`} className="mt-3"
            label="Shuffle items when the student sees them"
            checked={template.shuffleItems !== false}
            onChange={(e) => patch({ shuffleItems: e.target.checked })} />
          <Form.Text className="text-muted d-block mt-2">
            Partial credit: students score 1 per item placed in the correct bucket, divided by total items.
          </Form.Text>
        </div>
      ) : isCloze ? (
        <div>
          <h6 className="mt-2">Blanks</h6>
          {clozeMarkerMismatch ? (
            <Alert variant="warning" className="py-2">
              The prompt has <strong>{clozeMarkerCount}</strong> marker{clozeMarkerCount === 1 ? '' : 's'} but <strong>{(template.blanks || []).length}</strong> blank{(template.blanks || []).length === 1 ? '' : 's'} — students will see the mismatch on the player.
            </Alert>
          ) : null}
          {(template.blanks || []).map((blank, blankIndex) => (
            <Card key={blankIndex} className="mb-2 border-light bg-light">
              <Card.Body className="py-2">
                <Row className="g-2 align-items-center">
                  <Col xs="auto" className="fw-bold text-muted">{`{{${blankIndex + 1}}}`}</Col>
                  <Col>
                    <Form.Control
                      value={(blank.answers || []).join(', ')}
                      placeholder="Accepted answers, comma-separated (e.g. 4, four)"
                      onChange={(e) => updateBlank(blankIndex, {
                        answers: e.target.value.split(',').map((s) => s.trim()),
                      })}
                    />
                  </Col>
                  <Col xs="auto">
                    <Form.Check type="switch" id={`${idPrefix}-cloze-${blankIndex}-case`}
                      label="Case-sensitive"
                      checked={blank.caseSensitive === true}
                      onChange={(e) => updateBlank(blankIndex, { caseSensitive: e.target.checked })} />
                  </Col>
                  <Col xs="auto">
                    <Button type="button" size="sm" variant="outline-danger"
                      onClick={() => removeBlank(blankIndex)}
                      disabled={(template.blanks || []).length <= 1}>Del</Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          ))}
          <Button type="button" size="sm" variant="outline-secondary" onClick={addBlank}>
            <i className="bi bi-plus-lg me-1" />Add blank
          </Button>
          <Form.Text className="text-muted d-block mt-2">
            Partial credit: students score one mark per blank, divided by the total number of blanks. Leading / trailing whitespace is ignored; case-sensitive blanks must match exactly.
          </Form.Text>
        </div>
      ) : isTrueFalse ? (
        <div>
          <Form.Label className="mb-1">Correct answer</Form.Label>
          <div className="d-flex gap-3">
            <Form.Check type="radio" id={`${idPrefix}-tf-true`} name={`${idPrefix}-tf`}
              label="True"
              checked={template.correctAnswer === true}
              onChange={() => patch({ correctAnswer: true })} />
            <Form.Check type="radio" id={`${idPrefix}-tf-false`} name={`${idPrefix}-tf`}
              label="False"
              checked={template.correctAnswer === false}
              onChange={() => patch({ correctAnswer: false })} />
          </div>
        </div>
      ) : (
        <>
          {supportsFraming ? (
            <Form.Group className="mb-3">
              <Form.Label>Framing</Form.Label>
              <Form.Select value={template.framing || 'standard'}
                onChange={(e) => patch({ framing: e.target.value })}>
                {FRAMINGS.map((f) => (
                  <option key={f} value={f}>{FRAMING_LABELS[f]}</option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                Adds a "Pick the BEST / WORST answer" banner above the options. Useful when more than one option is reasonable but one is clearly preferable (or clearly wrong).
              </Form.Text>
            </Form.Group>
          ) : null}
          <h6 className="mt-2">Options {isMulti ? '(tick every correct option)' : '(mark the correct one)'}</h6>
          {(template.options || []).map((opt, optionIndex) => (
            <Row key={optionIndex} className="g-2 align-items-center mb-2">
              <Col xs="auto">
                {isMulti ? (
                  <Form.Check type="checkbox"
                    id={`${idPrefix}-correct-${optionIndex}`}
                    checked={(template.correctIndices || []).includes(optionIndex)}
                    onChange={() => toggleMultiCorrect(optionIndex)}
                    label={<span className="visually-hidden">Mark option {optionIndex + 1} as correct</span>} />
                ) : (
                  <Form.Check type="radio"
                    name={`${idPrefix}-correct`}
                    id={`${idPrefix}-correct-${optionIndex}`}
                    checked={Number(template.correctIndex) === optionIndex}
                    onChange={() => patch({ correctIndex: optionIndex })}
                    label={<span className="visually-hidden">Mark option {optionIndex + 1} as correct</span>} />
                )}
              </Col>
              <Col>
                <Form.Control value={opt} placeholder={`Option ${optionIndex + 1}`}
                  onChange={(e) => setOption(optionIndex, e.target.value)} />
              </Col>
              <Col xs="auto">
                <Button type="button" size="sm" variant="outline-danger" onClick={() => removeOption(optionIndex)} disabled={(template.options || []).length <= 2}>
                  Del
                </Button>
              </Col>
            </Row>
          ))}
          <Button type="button" size="sm" variant="outline-secondary" onClick={addOption} disabled={(template.options || []).length >= 8}>Add option</Button>
          <Form.Check type="switch" id={`${idPrefix}-shuffle`} className="mt-3"
            label="Shuffle option order each session (so students can't memorise positions)"
            checked={template.shuffleOptions !== false}
            onChange={(e) => patch({ shuffleOptions: e.target.checked })} />
          {isMulti ? (
            <Form.Text className="text-muted d-block mt-2">
              Partial credit: students score <code>(correct picks − incorrect picks) / total correct</code>, clipped at 0. Picking everything therefore scores 0.
            </Form.Text>
          ) : null}
        </>
      )}

      <Row className="g-3 mt-1">
        <Form.Group as={Col} md={12}>
          <Form.Label>Teaching note (optional, shown after answer)</Form.Label>
          <Form.Control as="textarea" rows={1} value={template.notes || ''}
            onChange={(e) => patch({ notes: e.target.value })}
            placeholder="The first-line empirical regimen here is doxycycline plus levofloxacin; cefuroxime is contraindicated in true penicillin allergy." />
        </Form.Group>
      </Row>
    </>
  );
};

export default QbankTemplateForm;
